// We should not pass BIG arrays to Firebase functions - they serialise as JSON maps - with position! So VERY inefficient
import { httpsCallable, httpsCallableFromURL } from "firebase/functions";
import { FirebaseServices } from "./config";
import { trackPerformanceMetrics } from "../analytics";

//        const str = uint8ToBase64(args.contents);
export function uint8ToBase64(buffer: ArrayBuffer): string {
  var binary = "";
  var bytes = new Uint8Array(buffer);
  var len = bytes.byteLength;
  for (var i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

export function getFunction(fb: FirebaseServices, name: string, regionOrCustomUrl?: string) {
  // Passing in the region appears to override the emulator! So instead we will set the region after...
  // const functions = getFunctions(app, regionOrCustomUrl);
  const functions = fb.functions;
  const target = regionOrCustomUrl || fb.regionOrCustomUrl || "europe-north1";

  //TODO: Set somewhere else!?!
  if (target.startsWith("http")) {
    //functions.customDomain = target;
    const url = `${target}/${name}`;
    return httpsCallableFromURL(functions, url);
  } else {
    functions.region = target;
    return httpsCallable(functions, name);
  }
}

/**
 * Invokes a Firebase Cloud Function by name using standard configuration.
 *
 * @param fb - The Firebase services configuration object.
 * @param name - The name of the Cloud Function to invoke.
 * @param params - Optional arguments to send to the function.
 * @returns A promise resolving to the function's output.
 */
export async function callFunction<TIn, TOut>(fb: FirebaseServices, name: string, params?: TIn): Promise<TOut> {
  console.log("Calling", name, fb.regionOrCustomUrl);
  return callFunction2(fb, name, fb.regionOrCustomUrl, params);
}

/**
 * Normalizes Firebase errors, translating cryptic "functions/internal" / "Internal" errors
 * caused by Android device sleep, background socket termination, or network aborts into
 * clear, actionable error messages.
 *
 * @param error - The raw error thrown by Firebase Callable Functions.
 * @param functionName - The name of the Cloud Function that was called.
 * @returns A normalized Error instance.
 */
export function normalizeCallableError(error: any, functionName: string): Error {
  if (!error) return new Error("Unknown error");
  const code = (error.code || "").toLowerCase();
  const rawMsg = (error.message || "").trim();
  const isInternal = code === "functions/internal" || code === "internal" || rawMsg.toLowerCase() === "internal";
  const isNetworkDropout =
    isInternal ||
    rawMsg.toLowerCase().includes("network") ||
    rawMsg.toLowerCase().includes("abort") ||
    rawMsg.toLowerCase().includes("connection") ||
    rawMsg.toLowerCase().includes("socket") ||
    code.includes("unavailable") ||
    code.includes("deadline-exceeded");

  if (isNetworkDropout) {
    const isUpload = functionName.toLowerCase().includes("media") || functionName.toLowerCase().includes("upload");
    const friendlyMessage = isUpload
      ? "Upload interrupted (device entered sleep mode or connection was lost). Please keep the screen on while uploading, or tap Retry."
      : `Network connection interrupted while calling ${functionName}. Please check your connection and try again.`;

    const normalizedError = new Error(friendlyMessage);
    (normalizedError as any).code = error.code || "functions/internal";
    (normalizedError as any).isInterrupted = true;
    (normalizedError as any).originalError = error;
    return normalizedError;
  }

  return error;
}

/**
 * Invokes a Firebase Cloud Function at a specific region or custom URL,
 * recording latency metrics for the call.
 *
 * @param fb - The Firebase services configuration object.
 * @param name - The name of the Cloud Function to invoke.
 * @param regionOrCustomUrl - The custom URL or region to target.
 * @param params - Optional arguments to send to the function.
 * @returns A promise resolving to the function's output.
 */
export async function callFunction2<TIn, TOut>(
  fb: FirebaseServices,
  name: string,
  regionOrCustomUrl: string,
  params?: TIn
): Promise<TOut> {
  const func = getFunction(fb, name, regionOrCustomUrl);
  const startTime = performance.now();
  try {
    const response = await func(params);
    const duration = performance.now() - startTime;
    console.info(`[Backend Metric] ${name} took ${duration.toFixed(2)}ms`);
    console.debug("Firebase", name, { status: (response?.data as any)?.status ?? true });
    trackPerformanceMetrics({
      apiLatencyMs: duration,
      apiFunction: name,
    });
    return response.data as TOut;
  } catch (error: any) {
    const duration = performance.now() - startTime;
    console.error(`[Backend Metric] ${name} failed after ${duration.toFixed(2)}ms`, error);
    trackPerformanceMetrics({
      apiLatencyMs: duration,
      apiFunction: name,
    });
    throw normalizeCallableError(error, name);
  }
}

